// §IV number-plaque placement — the F4 collision rule, ported verbatim from the CD canon
// board `R10 - §IV - canon F10.html` (chart() byte-identical to F9) (`chart()`, ~L388–472). Registered by decision s714.d1
// (Kirill 2026-09-09: "ну да, надо перерисовывать"); the rule itself is s678.d1 (principle) +
// s681.d1 (mechanics), numbers pinned in Field Mapping Spec §IV.
//
// WHY THIS MODULE EXISTS — the SSR/jsdom path-measurement problem (cc-76):
// CD's board computes tab positions at BROWSER runtime and samples the smoothed red line with
// `getTotalLength`/`getPointAtLength` (SVGGeometryElement). SectionIV renders under SSR
// (renderToStaticMarkup) and the byte-parity golden is captured in jsdom — NEITHER implements
// those methods, so a verbatim port would throw and take the whole assembled document down
// (the cc-30 crash class). `samplePath` below is a pure-JS arc-length sampler that replaces
// `getPointAtLength`; it is shared by (a) this SSR placement and (b) the jsdom polyfill in
// `scripts/capture-svg-golden-iv.mjs`, so the golden and the live render sample the SAME curve
// by construction. Fidelity to CD's real-browser canon is proven not by the sampler's absolute
// coordinates (the printed tab coords come from `cand(side,tier)`, pure arithmetic) but by the
// SELECTION it makes: the ported rule must reproduce CD's stated oracle — 78 plaques, 15
// deviations from base (13 side-flips + 2 tiers) on 14 boards, data-tab-fallback nowhere.
// web-Claude independently confirmed a cubic sampler reproduces that oracle (ticket s715/s716).

// --- rule numbers (Field Mapping Spec §IV; cited F4-board cap-board-15-08) ---
export const DOT_BOX = 11;   // half-extent of a carrier point's exclusion box (full 22×22)
export const TAB_W = 26;
export const TAB_H = 16;
export const MARG = 4;       // gap to a neighbour plaque / the stage-label band
export const TIER = 22;      // vertical step per push
export const SAMPLE_STEP = 6; // arc-length step when sampling the red line into curve segments
// clearances by obstacle class (hard()) — four distinct gaps, not one (Field Mapping Spec §IV,
// corrected s713): neighbour plaque & label band = MARG (4), leader = GAP_LEADER (2), red line =
// GAP_CURVE (5), point box = 0 (the whole clearance sits inside DOT_BOX, tested without inflation).
export const GAP_LEADER = 2;
export const GAP_CURVE = 5;
export const GAP_POINT = 0;

// --- intersection primitives (verbatim CD) ---
export const INFL = (r, m) => ({ x0: r.x0 - m, x1: r.x1 + m, y0: r.y0 - m, y1: r.y1 + m });
export const RHIT = (a, b) => a.x0 < b.x1 && b.x0 < a.x1 && a.y0 < b.y1 && b.y0 < a.y1;

// Liang–Barsky segment×rect (does segment s clip against rect r).
export function SEGRECT(s, r) {
  let t0 = 0, t1 = 1;
  const dx = s.x2 - s.x1, dy = s.y2 - s.y1;
  const clip = (p, q) => {
    if (p === 0) return q >= 0;
    const t = q / p;
    if (p < 0) { if (t > t1) return false; if (t > t0) t0 = t; }
    else { if (t < t0) return false; if (t < t1) t1 = t; }
    return true;
  };
  return clip(-dx, s.x1 - r.x0) && clip(dx, r.x1 - s.x1) && clip(-dy, s.y1 - r.y0) && clip(dy, r.y1 - s.y1) && t0 <= t1;
}

// Segment×segment proper intersection (verbatim CD).
export function SEGSEG(a, b) {
  const d = (p, q, r) => (q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x);
  const A = { x: a.x1, y: a.y1 }, B = { x: a.x2, y: a.y2 }, C = { x: b.x1, y: b.y1 }, D = { x: b.x2, y: b.y2 };
  const d1 = d(C, D, A), d2 = d(C, D, B), d3 = d(A, B, C), d4 = d(A, B, D);
  return ((d1 > 0) !== (d2 > 0)) && ((d3 > 0) !== (d4 > 0));
}

// --- pure-JS arc-length sampler (replaces getTotalLength/getPointAtLength) ---
//
// Parses a smoothed path (`M x,y` then repeated ` C c1 c2 end`, the only shape `smooth()`
// emits), flattens each cubic to a dense polyline, and walks it by arc length. Subdivision is
// proportional to chord length so long stage gaps stay accurate; the residual vs a browser's
// analytic getPointAtLength is sub-pixel and — validated against the oracle — flips no selection.
function flatten(d) {
  const toks = d.match(/[MCLZ]|-?\d*\.?\d+(?:e[+-]?\d+)?/gi);
  if (!toks) return [];
  const pts = [];
  let i = 0, cx = 0, cy = 0;
  const num = () => parseFloat(toks[i++]);
  while (i < toks.length) {
    const cmd = toks[i++];
    if (cmd === 'M') { cx = num(); cy = num(); pts.push({ x: cx, y: cy }); }
    else if (cmd === 'L') { cx = num(); cy = num(); pts.push({ x: cx, y: cy }); }
    else if (cmd === 'C') {
      const x1 = num(), y1 = num(), x2 = num(), y2 = num(), ex = num(), ey = num();
      const chord = Math.hypot(ex - cx, ey - cy) + Math.hypot(x1 - cx, y1 - cy) + Math.hypot(x2 - x1, y2 - y1);
      const steps = Math.max(24, Math.ceil(chord / 1.5));
      for (let s = 1; s <= steps; s++) {
        const t = s / steps, u = 1 - t;
        const x = u * u * u * cx + 3 * u * u * t * x1 + 3 * u * t * t * x2 + t * t * t * ex;
        const y = u * u * u * cy + 3 * u * u * t * y1 + 3 * u * t * t * y2 + t * t * t * ey;
        pts.push({ x, y });
      }
      cx = ex; cy = ey;
    }
    // Z/others not emitted by smooth()
  }
  return pts;
}

// Sample the path at every `step` of arc length; mirrors CD `pathPts` exactly (0..L by step,
// then the endpoint appended). Returns [{x,y}].
export function samplePath(d, step) {
  const pts = flatten(d);
  if (pts.length < 2) return pts.slice();
  const cum = [0];
  for (let i = 1; i < pts.length; i++) cum.push(cum[i - 1] + Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y));
  const L = cum[cum.length - 1];
  const at = (l) => {
    if (l <= 0) return { x: pts[0].x, y: pts[0].y };
    if (l >= L) return { x: pts[pts.length - 1].x, y: pts[pts.length - 1].y };
    // linear scan (polylines here are short; keep it simple and deterministic)
    let i = 1;
    while (i < cum.length && cum[i] < l) i++;
    const seg = cum[i] - cum[i - 1];
    const f = seg > 0 ? (l - cum[i - 1]) / seg : 0;
    return { x: pts[i - 1].x + (pts[i].x - pts[i - 1].x) * f, y: pts[i - 1].y + (pts[i].y - pts[i - 1].y) * f };
  };
  const out = [];
  for (let l = 0; l <= L; l += step) out.push(at(l));
  out.push(at(L));
  return out;
}

// getTotalLength/getPointAtLength backing for the jsdom polyfill in the capture script.
export function pathGeom(d) {
  const pts = flatten(d);
  const cum = [0];
  for (let i = 1; i < pts.length; i++) cum.push(cum[i - 1] + Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y));
  const L = pts.length ? cum[cum.length - 1] : 0;
  return {
    total: L,
    at(l) {
      if (pts.length === 0) return { x: 0, y: 0 };
      if (l <= 0) return { x: pts[0].x, y: pts[0].y };
      if (l >= L) return { x: pts[pts.length - 1].x, y: pts[pts.length - 1].y };
      let i = 1;
      while (i < cum.length && cum[i] < l) i++;
      const seg = cum[i] - cum[i - 1];
      const f = seg > 0 ? (l - cum[i - 1]) / seg : 0;
      return { x: pts[i - 1].x + (pts[i].x - pts[i - 1].x) * f, y: pts[i - 1].y + (pts[i].y - pts[i - 1].y) * f };
    },
  };
}

// --- the placement rule ---
//
// Returns one pick per carrier index, in order. Each pick carries everything the renderer needs:
// { n, tier, side, dx, cx, rx, dy, x, y, flip, r, s, soft?, fallback? }.
// `cols`/`cf` are indexed by stage; `carrierIdx` are stage indices (already range-filtered by the
// caller's cc-30 guard). `yVal` and `G` are passed in to avoid a circular import with SectionIV.
export function placeTabs(carrierIdx, cols, cf, red, yVal, G) {
  const band = { x0: -1e4, x1: 1e4, y0: G.Y_TOP - 34, y1: G.Y_TOP - 12 }; // chart_stage_labels band
  const curve = [];
  { const smp = samplePath(red, SAMPLE_STEP); for (let i = 1; i < smp.length; i++) curve.push({ x1: smp[i - 1].x, y1: smp[i - 1].y, x2: smp[i].x, y2: smp[i].y }); }
  const dotBox = carrierIdx.map((i) => { const px = cols[i].xMid, py = yVal(cf[i]); return { x0: px - DOT_BOX, x1: px + DOT_BOX, y0: py - DOT_BOX, y1: py + DOT_BOX }; });
  const placed = [band], leaders = [];
  // hard: plaque touches nothing — neighbours, their leaders, points, red line (gap 5), label band.
  const hard = (r, sg) => {
    let n = 0;
    placed.forEach((p) => { if (RHIT(INFL(r, MARG), p)) n++; });
    leaders.forEach((l) => { if (SEGRECT(l, INFL(r, GAP_LEADER))) n++; });
    placed.forEach((p) => { if (SEGRECT(sg, INFL(p, GAP_LEADER))) n++; });
    leaders.forEach((l) => { if (SEGSEG(sg, l)) n++; });
    dotBox.forEach((b) => { if (RHIT(r, b)) n++; }); // GAP_POINT = 0: no inflation
    if (curve.some((c) => SEGRECT(c, INFL(r, GAP_CURVE)))) n++;
    return n;
  };
  // soft: a leader crossing the red line is ugly but may have no alternative — second sort key.
  const soft = (sg) => curve.reduce((n, c) => n + (SEGSEG(sg, c) ? 1 : 0), 0);
  return carrierIdx.map((idx, j) => {
    const x = cols[idx].xMid, y = yVal(cf[idx]);
    const flip = y < (G.Y_TOP + 25), pref = j % 2 === 0 ? -1 : 1;
    const cand = (side, tier) => {
      const dx = side * 28, cx = x + dx, rx = dx > 0 ? 0 : -26, base = flip ? 38 : -36;
      const dy = base + (flip ? tier * TIER : -tier * TIER);
      return {
        dx, cx, rx, dy, tier, side,
        r: { x0: cx + rx, x1: cx + rx + TAB_W, y0: y + dy - 9, y1: y + dy - 9 + TAB_H },
        s: { x1: cx, y1: y + dy + (flip ? -9 : 9), x2: x, y2: y + (flip ? 4 : -4) },
      };
    };
    const inFrame = (c) => c.r.x0 >= 2 && c.r.x1 <= G.W - 2 && c.r.y0 >= 2 && c.r.y1 <= G.H + 30;
    let pick;
    const list = [];
    [pref, -pref].forEach((side) => { for (let t = 0; t < 10; t++) list.push(cand(side, t)); });
    const ok = list.filter(inFrame);
    const pool = (ok.length ? ok : list).map((c) => Object.assign(c, { h: hard(c.r, c.s) }));
    const clean = pool.filter((c) => c.h === 0);
    if (clean.length) {
      clean.forEach((c) => { c.sf = soft(c.s); });
      pick = clean.slice().sort((a, b) => a.sf - b.sf || a.tier - b.tier)[0];
      if (pick.sf) pick.soft = pick.sf;
    } else {
      pick = pool.slice().sort((a, b) => a.h - b.h)[0];
      pick.fallback = pick.h;
    }
    placed.push(pick.r); leaders.push(pick.s);
    return { ...pick, n: String(j + 1).padStart(2, '0'), x, y, flip };
  });
}
